/**
 * Email validation regex pattern (RFC 5322 compliant)
 */
export const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Validate email address
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  return EMAIL_REGEX.test(email.trim());
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @param {function} t - Translator from useTranslation(); messages live in the
 *   `validation` catalog
 * @returns {object} - { valid: boolean, errors: string[] }
 */
export const validatePassword = (password, t) => {
  const errors = [];

  if (!password || password.length < 8) {
    errors.push(t('validation:password.min_length_long'));
  }

  if (!/[a-z]/.test(password)) {
    errors.push(t('validation:password.lowercase'));
  }

  if (!/[A-Z]/.test(password)) {
    errors.push(t('validation:password.uppercase'));
  }

  if (!/[0-9]/.test(password)) {
    errors.push(t('validation:password.number'));
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Get password strength level
 * @param {string} password - Password to evaluate
 * @returns {string} - 'weak', 'medium', or 'strong'
 */
export const getPasswordStrength = (password) => {
  if (!password) return 'weak';

  let strength = 0;

  // Length
  if (password.length >= 8) strength++;
  if (password.length >= 12) strength++;

  // Character types
  if (/[a-z]/.test(password)) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[^a-zA-Z0-9]/.test(password)) strength++;

  if (strength <= 2) return 'weak';
  if (strength <= 4) return 'medium';
  return 'strong';
};

/**
 * Validate required field
 * @param {any} value - Value to validate
 * @returns {boolean} - True if not empty, false otherwise
 */
export const isRequired = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

/**
 * Validate minimum length
 * @param {string} value - String to validate
 * @param {number} minLength - Minimum required length
 * @returns {boolean}
 */
export const minLength = (value, minLength) => {
  if (!value) return false;
  return value.length >= minLength;
};

/**
 * Validate maximum length
 * @param {string} value - String to validate
 * @param {number} maxLength - Maximum allowed length
 * @returns {boolean}
 */
export const maxLength = (value, maxLength) => {
  if (!value) return true; // Empty is valid for maxLength check
  return value.length <= maxLength;
};

/**
 * Validate number range
 * @param {number} value - Number to validate
 * @param {number} min - Minimum value (inclusive)
 * @param {number} max - Maximum value (inclusive)
 * @returns {boolean}
 */
export const isInRange = (value, min, max) => {
  const num = Number(value);
  if (isNaN(num)) return false;
  return num >= min && num <= max;
};

/**
 * Validate positive number
 * @param {number} value - Number to validate
 * @returns {boolean}
 */
export const isPositiveNumber = (value) => {
  const num = Number(value);
  return !isNaN(num) && num > 0;
};

/**
 * Validate non-negative number
 * @param {number} value - Number to validate
 * @returns {boolean}
 */
export const isNonNegativeNumber = (value) => {
  const num = Number(value);
  return !isNaN(num) && num >= 0;
};

/**
 * Validate integer
 * @param {number} value - Number to validate
 * @returns {boolean}
 */
export const isInteger = (value) => {
  const num = Number(value);
  return !isNaN(num) && Number.isInteger(num);
};

/**
 * Validate UUID format
 * @param {string} value - String to validate
 * @returns {boolean}
 */
export const isUUID = (value) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
};

/**
 * Validate URL format
 * @param {string} value - String to validate
 * @returns {boolean}
 */
export const isValidURL = (value) => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

/**
 * Sanitize string input (remove HTML tags)
 * @param {string} value - String to sanitize
 * @returns {string} - Sanitized string
 */
export const sanitizeString = (value) => {
  if (!value || typeof value !== 'string') return '';
  return value.replace(/<[^>]*>/g, '').trim();
};

/**
 * Validation rules for React Hook Form.
 *
 * Takes the `t` from any component's useTranslation() so the messages come
 * from the `validation` catalog in the viewer's language:
 *
 *   const rules = validationRules(t);
 *   register("email", rules.email);
 */
export const validationRules = (t) => ({
  email: {
    required: t('validation:email.required'),
    pattern: {
      value: EMAIL_REGEX,
      message: t('validation:email.invalid'),
    },
  },
  password: {
    required: t('validation:password.required'),
    minLength: {
      value: 8,
      message: t('validation:password.min_length'),
    },
  },
  confirmPassword: (watchField) => ({
    required: t('validation:confirm_password.required'),
    validate: (value) => value === watchField || t('validation:confirm_password.mismatch'),
  }),
  required: {
    required: t('validation:required'),
  },
  positiveNumber: {
    required: t('validation:required'),
    validate: (value) => isPositiveNumber(value) || t('validation:positive_number'),
  },
  nonNegativeNumber: {
    required: t('validation:required'),
    validate: (value) => isNonNegativeNumber(value) || t('validation:non_negative_number'),
  },
});
